import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Edit, Trash2, RefreshCw, Copy, FileUp, ExternalLink, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ActionsMenuProps {
  onModify: () => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onCheckStatus?: () => void;
  onPromoteToDraft?: () => void;
  status?: string;
  className?: string;
  menuPosition?: 'top' | 'bottom';
  isAnyProcessing?: boolean;
  // External posts (source === 'external') are read-only mirrors of what
  // the user published directly on the platform. Modify/Delete/Duplicate
  // are disabled; Open (link out) and Hide (dismiss from My Content) take
  // their place.
  isExternal?: boolean;
  externalPermalink?: string | null;
  onHide?: () => void;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  onModify,
  onDelete,
  onDuplicate,
  onCheckStatus,
  onPromoteToDraft,
  status,
  className = '',
  menuPosition = 'bottom',
  isAnyProcessing = false,
  isExternal = false,
  externalPermalink = null,
  onHide
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate position when menu opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 173; // 20% wider than 144px
      
      // Always position below the button regardless of menuPosition prop
      setMenuStyle({
        position: 'fixed',
        left: `${buttonRect.left - menuWidth + buttonRect.width}px`,
        top: `${buttonRect.bottom + 8}px`,
        width: `${menuWidth}px`,
        zIndex: 60
      });
    }
  }, [isOpen, menuPosition]);

  const hasCheckStatusHandler = Boolean(onCheckStatus);
  const isProcessing = Boolean(isAnyProcessing);
  
  if (!onModify && !onDelete && !onDuplicate && !onCheckStatus) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click event
          setIsOpen(!isOpen);
        }}
        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Actions"
      >
        <MoreVertical className="w-5 h-5 text-gray-500" />
      </button>

      {isOpen && (
        <div style={menuStyle} className="rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="py-1">
            {isExternal && (
              <>
                {externalPermalink && (
                  <a
                    href={externalPermalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                    <span>{t('common.openOnPlatform', 'Ouvrir sur la plateforme')}</span>
                  </a>
                )}
                {onHide && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onHide();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <EyeOff className="w-4 h-4 text-gray-500" />
                    <span>{t('common.hide', 'Masquer')}</span>
                  </button>
                )}
                <div className="border-t border-gray-100 my-1" />
                <div className="px-4 py-2 text-xs text-gray-400 italic">
                  {t('common.externalPostReadOnly', 'Publié hors Socialness — lecture seule')}
                </div>
              </>
            )}
            {!isExternal && isProcessing && onCheckStatus && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  onCheckStatus();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
              >
                <RefreshCw className="w-4 h-4 text-blue-500" />
                <span>{t('common.checkPostStatus', 'Check Post Status')}</span>
              </button>
            )}
            {!isExternal && onPromoteToDraft && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPromoteToDraft();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
              >
                <FileUp className="w-4 h-4 text-amber-500" />
                <span>{t('common.promoteToDraft', 'Promote to Draft')}</span>
              </button>
            )}
            {!isExternal && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onModify();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Edit className="w-4 h-4 text-gray-500" />
                <span>{t('common.modify')}</span>
              </button>
            )}
            {!isExternal && onDuplicate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Copy className="w-4 h-4 text-gray-500" />
                <span>{t('common.duplicate', 'Duplicate')}</span>
              </button>
            )}
            {!isExternal && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
                <span>{t('common.delete')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 